import { FilterParams } from "src/library/utils";
import { IProduct } from "src/core/products/api/products.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  RequestConfig,
  UseApiRequest,
  EndpointConfig
} from "src/core/shared/api/config.api";

// type allows for id and promotionalTagId to be optional for create purposes
export type CreateEditProductPromotionalTag =
  Omit<IProductPromotionalTag, "id" | "promotionalTagId"> &
  Partial<Pick<IProductPromotionalTag, "id" | "promotionalTagId">>;

// type allows for id optional for create purposes
// also allows for the create state for productPromotionalTags
export type CreateEditPromotionalTag =
  Omit<IPromotionalTag, "productPromotionalTags" | "id"> &
  Partial<Pick<IPromotionalTag, "id">> &
  {
    productPromotionalTags?: CreateEditProductPromotionalTag[]
  };

export type CreatePromotionalTagParams = CreateEditPromotionalTag;
export type BulkUpdatePromotionalTagParams = { data: PartialPromotionalTag };
export type PartialPromotionalTag = Partial<IPromotionalTag>;
export type UpdatePromotionalTagParams = { promotionalTagId: number, data: Partial<CreateEditPromotionalTag> };
export type ListPromotionalTagParams = {
  filters: FilterParams<IPromotionalTag>;
};

export interface IProductPromotionalTag {
  id: number;
  productId: number;
  promotionalTagId: number;
  startDate: Date | string;
  endDate?: Date | string | null;
  promotionalTag?: IPromotionalTag;
  product?: IProduct;
}
export interface PromotionalTagData {
  data: IPromotionalTag[];
}

export interface IPromotionalTag {
  id: number;
  label: string;
  color: string;
  priority: number;
  productPromotionalTags?: IProductPromotionalTag[];
}

export enum PromotionalTagRequestName {
  CREATE_PROMOTIONAL_TAG = "CREATE_PROMOTIONAL_TAG",
  GET_PROMOTIONAL_TAGS = "GET_PROMOTIONAL_TAG",
  GET_PRODUCTS_BY_PROMOTIONAL_TAG = "GET_PRODUCTS_BY_PROMOTIONAL_TAG",
  DELETE_PROMOTIONAL_TAG = "DELETE_PROMOTIONAL_TAG",
  UPDATE_PROMOTIONAL_TAG = "UPDATE_PROMOTIONAL_TAG",
  BULK_UPDATE_PROMOTIONAL_TAG = "BULK_UPDATE_PROMOTIONAL_TAG",
  GET_PROMOTIONAL_TAGS_BY_BRANCH = "GET_PROMOTIONAL_TAGS_BY_BRANCH"
}

type PromotionalTagEndpointConfig = {
  [PromotionalTagRequestName.CREATE_PROMOTIONAL_TAG]: EndpointConfig<
    CreatePromotionalTagParams,
    IPromotionalTag
  >,
  [PromotionalTagRequestName.GET_PROMOTIONAL_TAGS]: EndpointConfig<
    {filters?: FilterParams<IPromotionalTag>;},
    IPromotionalTag[]
  >,
  [PromotionalTagRequestName.GET_PRODUCTS_BY_PROMOTIONAL_TAG]: EndpointConfig<
    {id: number},
    IPromotionalTag
  >,
  [PromotionalTagRequestName.DELETE_PROMOTIONAL_TAG]: EndpointConfig<
    {id: number},
    IPromotionalTag
  >,
  [PromotionalTagRequestName.UPDATE_PROMOTIONAL_TAG]: EndpointConfig<
    UpdatePromotionalTagParams,
    IPromotionalTag
  >,
  [PromotionalTagRequestName.BULK_UPDATE_PROMOTIONAL_TAG]: EndpointConfig<
  PromotionalTagData,
  IPromotionalTag[]
  >,
  [PromotionalTagRequestName.GET_PROMOTIONAL_TAGS_BY_BRANCH]: EndpointConfig<
  {branchId: number},
  IPromotionalTag[]
  >,
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/promotion/promotional-tags`;

/**
 * promotionalTag API
 */
const requests: RequestConfig<PromotionalTagEndpointConfig> = {
  [ PromotionalTagRequestName.CREATE_PROMOTIONAL_TAG ]: data => ({
    url: `${BASE_URL}/create`,
    method: "POST",
    data
  }),
  [ PromotionalTagRequestName.GET_PROMOTIONAL_TAGS ]: ({ filters }) => ({
    url: `${BASE_URL}/all/tags`,
    method: "GET",
    filters
  }),
  [ PromotionalTagRequestName.GET_PRODUCTS_BY_PROMOTIONAL_TAG ]: ({ id }) => ({
    url: `${BASE_URL}/${id}`,
    method: "GET"
  }),
  [ PromotionalTagRequestName.DELETE_PROMOTIONAL_TAG ]: ({ id }) => ({
    url: `${BASE_URL}/${id}`,
    method: "DELETE"
  }),
  [ PromotionalTagRequestName.UPDATE_PROMOTIONAL_TAG ]: ({ promotionalTagId, data }) => ({
    url: `${BASE_URL}/${promotionalTagId}`,
    method: "PATCH",
    data
  }),
  [ PromotionalTagRequestName.BULK_UPDATE_PROMOTIONAL_TAG ]: ({ ...data }) => ({
    url: `${BASE_URL}/bulk/update/promotional-tags`,
    method: "PATCH",
    data
  }),
  [ PromotionalTagRequestName.GET_PROMOTIONAL_TAGS_BY_BRANCH ]: ({ branchId }) => ({
    url: `${BASE_URL}/branch/${branchId}`,
    method: "GET"
  })
};

export const usePromotionalTagApi: UseApiRequest<PromotionalTagEndpointConfig> = name => useAxios(requests[ name ], name);
